import { useState, useEffect, useCallback } from 'react';
import { useApp } from '../../context';
import api from '../../hooks/useApi';
import { parseRoles, canAccess } from '../../utils/rbac';
import { formatDate } from '../../utils/format';

const CCN_STATUS_LABELS = { draft: 'Draft', pending_approval: 'Under Review', client_feedback: 'Client Feedback', approved: 'Approved', rejected: 'Rejected' };
const CCN_STATUS_COLORS = { draft: 'bg-gray-100 text-gray-600', pending_approval: 'bg-amber-100 text-amber-700', client_feedback: 'bg-purple-100 text-purple-700', approved: 'bg-emerald-100 text-emerald-700', rejected: 'bg-red-100 text-red-700' };
const CCN_TYPE_LABELS = { budget: 'Budget', schedule: 'Schedule', scope: 'Scope', resource: 'Resource' };
const CCN_TYPE_COLORS = { budget: 'bg-gold/20 text-yellow-800', schedule: 'bg-blue-100 text-blue-700', scope: 'bg-purple-100 text-purple-700', resource: 'bg-teal-100 text-teal-700' };

function fmt(v) {
  if (v == null) return '—';
  const n = Number(v);
  if (isNaN(n)) return '—';
  return '£' + n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtDelta(before, after) {
  if (before == null || after == null) return null;
  const d = Number(after) - Number(before);
  return (d >= 0 ? '+' : '') + '£' + d.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtDate(s) { return s ? new Date(s).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'; }

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'pending_approval', label: 'Under Review' },
  { value: 'client_feedback', label: 'Client Feedback' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

export default function ChangeRegisterPage() {
  const { currentUser } = useApp();
  const [ccns, setCcns] = useState([]);
  // CCN values are SOW money, the sow:see-financials grant; the API leaves them
  // out for anybody else (pen test AD26341, AD12/AD16), so the value cards and
  // columns are not offered either.
  const seesMoney = canAccess('sow:see-financials');
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ sow_status: 'Active' });
      if (statusFilter) params.set('status', statusFilter);
      const data = await api.get(`ccns?${params}`);
      setCcns(data.ccns || []);
    } catch (e) {
      setCcns([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const filtered = ccns.filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (c.ccn_number || '').toLowerCase().includes(q) ||
      (c.title || '').toLowerCase().includes(q) ||
      (c.sow_ref || '').toLowerCase().includes(q) ||
      (c.client_name || '').toLowerCase().includes(q)
    );
  });

  // Summary stats
  const totalValueChange = filtered
    .filter(c => c.status === 'approved' && c.budget_before != null && c.budget_after != null)
    .reduce((sum, c) => sum + (Number(c.budget_after) - Number(c.budget_before)), 0);

  const pendingValueChange = filtered
    .filter(c => ['pending_approval', 'client_feedback'].includes(c.status) && c.budget_before != null && c.budget_after != null)
    .reduce((sum, c) => sum + (Number(c.budget_after) - Number(c.budget_before)), 0);

  const totalCcns = filtered.length;
  const approvedCount = filtered.filter(c => c.status === 'approved').length;
  const pendingCount = filtered.filter(c => ['pending_approval', 'client_feedback'].includes(c.status)).length;
  const draftCount = filtered.filter(c => c.status === 'draft').length;

  return (
    <div className="p-6">
      {/* Page header */}
      <div className="mb-6">
        <h1 className="text-xl font-extrabold text-navy">Change Register</h1>
        <p className="text-[10px] text-gray-500 mt-0.5">All contract change notes across active SOWs — track value impacts and approval status</p>
      </div>

      {/* Motivational callout */}
      <div className="bg-gradient-to-r from-gold/10 to-amber-50 border border-gold/30 rounded-xl p-4 mb-6 flex items-start gap-4">
        <div className="text-2xl flex-shrink-0">💰</div>
        <div>
          <div className="text-[11px] font-extrabold text-amber-900">Raise changes formally — every change is an opportunity</div>
          <div className="text-[9px] text-amber-800 mt-0.5 leading-relaxed">
            Formal CCNs protect margin, create an audit trail, and ensure the client acknowledges scope. Draft a CCN now rather than absorbing costs informally.
            Approved CCNs with budget increases go straight to profit.
          </div>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-[8px] font-bold uppercase text-gray-400 tracking-wider mb-1">Total CCNs</div>
          <div className="text-[22px] font-extrabold text-navy">{totalCcns}</div>
          <div className="text-[8px] text-gray-400 mt-0.5">{draftCount} draft · {pendingCount} pending · {approvedCount} approved</div>
        </div>
        {seesMoney && <>
        <div className="bg-white rounded-xl border border-emerald-200 p-4">
          <div className="text-[8px] font-bold uppercase text-emerald-600 tracking-wider mb-1">Approved Value Change</div>
          <div className={`text-[22px] font-extrabold ${totalValueChange >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
            {totalValueChange >= 0 ? '+' : ''}{fmt(totalValueChange)}
          </div>
          <div className="text-[8px] text-gray-400 mt-0.5">from {approvedCount} approved CCNs</div>
        </div>
        <div className="bg-white rounded-xl border border-amber-200 p-4">
          <div className="text-[8px] font-bold uppercase text-amber-600 tracking-wider mb-1">Pending Value Change</div>
          <div className={`text-[22px] font-extrabold ${pendingValueChange >= 0 ? 'text-amber-700' : 'text-red-600'}`}>
            {pendingValueChange !== 0 ? (pendingValueChange >= 0 ? '+' : '') : ''}{fmt(pendingValueChange)}
          </div>
          <div className="text-[8px] text-gray-400 mt-0.5">from {pendingCount} pending CCNs</div>
        </div>
        </>}
        <div className="bg-white rounded-xl border border-purple-200 p-4">
          <div className="text-[8px] font-bold uppercase text-purple-600 tracking-wider mb-1">Client Feedback</div>
          <div className="text-[22px] font-extrabold text-purple-700">{filtered.filter(c => c.status === 'client_feedback').length}</div>
          <div className="text-[8px] text-gray-400 mt-0.5">awaiting client response</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <input
          className="form-input text-[9px] w-56"
          placeholder="Search CCN, title, SOW, client…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select className="form-select text-[9px]" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          {STATUS_FILTER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-16 text-[10px] text-gray-400">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16">
          <div className="text-4xl mb-3">📋</div>
          <div className="text-[10px] font-semibold text-gray-500">No change notes found</div>
          <div className="text-[9px] text-gray-400 mt-1">Raise CCNs from the Documents tab inside any active SOW.</div>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                {['CCN', 'SOW', 'Client', 'Title', 'Type(s)', 'Status', ...(seesMoney ? ['Value Before', 'Value After', 'Change'] : []), 'Requested By', 'Created By', 'Created', 'Approved By', ''].map((h, i) => (
                  <th key={i} className="px-3 py-2 text-left text-[7px] uppercase tracking-wider text-gray-400 font-bold border-b bg-gray-50 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(ccn => {
                const types = (ccn.change_types || '').split(',').filter(Boolean);
                const delta = ccn.budget_before != null && ccn.budget_after != null
                  ? Number(ccn.budget_after) - Number(ccn.budget_before) : null;
                return (
                  <tr key={ccn.id} className="hover:bg-gray-50 border-b border-gray-100">
                    <td className="px-3 py-2 text-[9px] font-extrabold text-navy whitespace-nowrap">{ccn.ccn_number}</td>
                    <td className="px-3 py-2 text-[9px] font-semibold text-gray-700 whitespace-nowrap">{ccn.sow_ref}</td>
                    <td className="px-3 py-2 text-[9px] text-gray-600 whitespace-nowrap">{ccn.client_name || '—'}</td>
                    <td className="px-3 py-2 text-[9px] text-gray-800 max-w-xs truncate">{ccn.title}</td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-1">
                        {types.map(t => (
                          <span key={t} className={`text-[7px] font-bold px-1.5 py-px rounded ${CCN_TYPE_COLORS[t] || 'bg-gray-100 text-gray-600'}`}>{CCN_TYPE_LABELS[t] || t}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className={`text-[7px] font-bold px-1.5 py-0.5 rounded ${CCN_STATUS_COLORS[ccn.status] || 'bg-gray-100 text-gray-600'}`}>{CCN_STATUS_LABELS[ccn.status] || ccn.status}</span>
                    </td>
                    {seesMoney && <>
                    <td className="px-3 py-2 text-[9px] text-gray-500 text-right whitespace-nowrap">{fmt(ccn.budget_before)}</td>
                    <td className="px-3 py-2 text-[9px] text-gray-700 font-semibold text-right whitespace-nowrap">{fmt(ccn.budget_after)}</td>
                    <td className="px-3 py-2 text-[9px] font-bold text-right whitespace-nowrap">
                      {delta != null ? (
                        <span className={delta > 0 ? 'text-emerald-600' : delta < 0 ? 'text-red-500' : 'text-gray-400'}>
                          {delta > 0 ? '+' : ''}{fmt(delta)}
                        </span>
                      ) : '—'}
                    </td>
                    </>}
                    <td className="px-3 py-2 text-[8px] text-gray-500 whitespace-nowrap">{ccn.requested_by_name || '—'}</td>
                    <td className="px-3 py-2 text-[8px] text-gray-500 whitespace-nowrap">{ccn.created_by_name || '—'}</td>
                    <td className="px-3 py-2 text-[8px] text-gray-400 whitespace-nowrap">{fmtDate(ccn.created_at)}</td>
                    <td className="px-3 py-2 text-[8px] text-gray-500 whitespace-nowrap">{ccn.approved_by_name || '—'}</td>
                    <td className="px-3 py-2">
                      <a
                        href={`#sow-${ccn.sow_id}`}
                        onClick={e => { e.preventDefault(); window.dispatchEvent(new CustomEvent('nav-to-sow', { detail: { sowId: ccn.sow_id } })); }}
                        className="text-[8px] text-blue-500 hover:underline whitespace-nowrap"
                      >View SOW ↗</a>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
